import { Link } from "react-router-dom";
import { ArrowUpRight, Check } from "lucide-react";
import { HERO_IMAGE, ABOUT_IMAGE, CONSULTING_IMG } from "@/lib/content";
import FinalCTA from "@/components/FinalCTA";

export default function About() {
  return (
    <>
      <section className="relative bg-[var(--tmg-ivory)] border-b border-[var(--tmg-line)]">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-20 pt-16 pb-20 sm:pt-24 sm:pb-28 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <p className="tmg-overline mb-6 tmg-fade-up">— About</p>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.04] tracking-tight tmg-fade-up-d1">
              Who we are.
            </h1>
            <p className="mt-8 text-base sm:text-lg text-[var(--tmg-muted)] leading-relaxed tmg-fade-up-d2">
              TMG Consulting Group is a Michigan-based consulting firm working
              where leadership, people, and operations meet. Our principal
              consultant has led poverty-alleviation and workforce programs,
              built the Rules of Money curriculum, and designed operating
              systems for small businesses and nonprofits.
            </p>
          </div>
          <div className="lg:col-span-5 tmg-fade-up-d2">
            <div className="relative aspect-[4/5] max-w-md mx-auto">
              <div aria-hidden className="absolute -inset-3 border border-[var(--tmg-gold)]" />
              <img
                src={ABOUT_IMAGE}
                alt="TMG Consulting Group — principal consultant"
                className="relative w-full h-full object-cover"
                data-testid="about-hero-image"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="tmg-section">
        <div className="tmg-container grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <p className="tmg-overline mb-5">— Philosophy</p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold leading-tight">
              Clarity, confidence, and{" "}
              <span className="italic font-light text-[var(--tmg-gold-soft)]">implementation.</span>
            </h2>
          </div>
          <div className="lg:col-span-7 lg:pl-12 border-l border-[var(--tmg-line)] space-y-6 text-base sm:text-lg text-[var(--tmg-muted)] leading-relaxed">
            <p>
              Every engagement is led by our principal consultant, with
              specialist partners brought in when a project calls for it.
            </p>
            <p>
              Our work is rooted in clarity, confidence, and implementation. We believe
              business owners and organizations should not feel left behind by technology
              or disconnected from their finances. Instead, they should be equipped to
              understand it, use it, and build with it.
            </p>
            <p className="text-[var(--tmg-obsidian)]">
              With experience in business coaching, nonprofit leadership, workforce
              programs, operations, and systems development, we bring a blend of
              strategy, warmth, structure, and innovation.
            </p>
          </div>
        </div>
      </section>

      <section className="tmg-section bg-[var(--tmg-ivory-2)]/50 border-y border-[var(--tmg-line)]">
        <div className="tmg-container grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6 order-2 lg:order-1">
            <p className="tmg-overline mb-5">— What we bring to the table</p>
            <h2 className="font-display text-3xl sm:text-4xl font-semibold leading-tight mb-8">
              Strategy meets systems.
            </h2>
            <ul className="space-y-4">
              {[
                "Business strategy & operations consulting",
                "AI education for beginners and business owners",
                "Workflow mapping & SOP development",
                "No-code & AI-assisted app builds",
                "Nonprofit & community education leadership",
                "Funding readiness & business credit education",
              ].map((p) => (
                <li key={p} className="flex items-start gap-3 text-base">
                  <Check className="w-5 h-5 mt-0.5 text-[var(--tmg-gold)] flex-shrink-0" />
                  <span>{p}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-6 order-1 lg:order-2">
            <img src={CONSULTING_IMG} alt="Business consulting" className="w-full aspect-[4/3] object-cover" />
          </div>
        </div>
      </section>

      <section className="tmg-section">
        <div className="tmg-container text-center max-w-3xl mx-auto">
          <p className="tmg-overline-gold mb-6">— Let's connect</p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold leading-tight mb-8">
            Ready for the next step?
          </h2>
          <Link to="/book-call" data-testid="about-book-call-btn" className="tmg-btn-primary">
            Book a Discovery Call <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      <FinalCTA />
    </>
  );
}
